import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useModalStore, useAuthStore, useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import ModelSelector from '@/components/Navigation/ModelSelector';
import ModelDropdown from '@/components/Dropdowns/ModelDropdown';
import ChecklistProgressPill from '@/components/Checklist/ChecklistProgressPill';

const ModelChatRouteNavigationHeaderRight = React.memo(({ modelId }) => {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const favoriteModelsCount = useAuthStore(state => state.user?.favoriteModelIds?.length || 0);
  const { isXS } = useResponsiveness();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const toggleFavoriteModel = useAuthStore(state => state.toggleFavoriteModel);
  const favoriteModelIds = useAuthStore(state => state.user?.favoriteModelIds) || [];
  const isSaved = favoriteModelIds.includes(modelId) || false;

  const handleToggleSave = useCallback(async () => {
    try {
      if (isGhost && favoriteModelsCount >= 1) {
        openModal('GhostSignupModal');
        return;
      }
      if (!isPlus && favoriteModelsCount >= 5) {
        openModal('PricingModal', {
          title: t('nav.routes.modelChat.saveFavoriteModels'),
          subtitle: t('nav.routes.modelChat.saveFavoriteModelsDesc')
        });
        return;
      }

      const success = await toggleFavoriteModel(modelId);
      if (success) {
        addToast(isSaved ? t('nav.routes.modelChat.removedFromFavorites') : t('nav.routes.modelChat.addedToFavorites'), 'success');
      } else {
        addToast(isSaved ? t('nav.routes.modelChat.failedToUnsave') : t('nav.routes.modelChat.failedToSave'), 'error');
      }
    } catch (error) {
      if (error?.name === 'ApiError' && error?.message) {
        addToast(error.message, 'error', error?.data?.actions);
      } else {
        addToast(isSaved ? t('nav.routes.modelChat.failedToUnsave') : t('nav.routes.modelChat.failedToSave'), 'error');
      }
    }
  }, [toggleFavoriteModel, modelId, isSaved, addToast, favoriteModelsCount, isGhost, isPlus, openModal, t]);

  if (isGhost) return null;

  const TriggerComponent = React.memo(() => (
    <NavigationBarButton
      icon="ellipsis"
      title={t('nav.routes.modelChat.moreOptions')}
      tooltip={t('nav.routes.modelChat.moreOptions')}
    />
  ));

  return (
    <NavigationBarButtonContainer rightPadding>
      {hasUser && !isXS && Platform.OS === 'web' && <ChecklistProgressPill />}
      {/* Star quick-save lives in the "more options" dropdown now (Save row),
          so the standalone header star is intentionally not rendered here. */}
      <ModelDropdown 
        modelId={modelId} 
        isSaved={isSaved}
        TriggerComponent={TriggerComponent}
        onToggleSave={handleToggleSave}
      />
    </NavigationBarButtonContainer>
  );
});

ModelChatRouteNavigationHeaderRight.displayName = 'ModelChatRouteNavigationHeaderRight';

// function ModelChatRouteNavigationHeaderLeft({ model }) {
//   return (
//     <DefaultRouteNavigationHeaderLeft>
//       <ModelSelector modelIds={[model?.id]} />
//     </DefaultRouteNavigationHeaderLeft>
//   )
// }

function getModelChatRouteNavigationOptions(model, isXS) {
  if (!model?.id) return {};

  const title = "New Chat";

  return {
    title,
    headerTitle: title,
    // headerLeft: () => <ModelChatRouteNavigationHeaderLeft model={model} />,
    headerRight: () => <ModelChatRouteNavigationHeaderRight modelId={model.id} />
  };
}

export default getModelChatRouteNavigationOptions; 